import { Lock, AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { useLockStore } from "../store/lockStore";
import { useLockActions } from "../hooks/useLockActions";
import { useAuth } from "../auth/AuthProvider";

interface LockScreenProps {
  formId: string;
  /**
   * Refetch form data when the lock screen recovers via fallback acquire
   * (lock TTL'd between page load and the user clicking Request Access).
   * Forwarded to useLockActions so its requestAccess fallback can refresh
   * the form before flipping to "locked".
   */
  onLockGranted?: () => Promise<void>;
}

const HEADING_ID = "lock-screen-heading";

/**
 * Full-page overlay shown when another user holds the lock. The form is not
 * rendered behind it — there's nothing the caller can do to the form until
 * they acquire the lock via the Request Access flow.
 */
export function LockScreen({ formId, onLockGranted }: LockScreenProps) {
  const lockedBy = useLockStore((s) => s.lockedBy);
  const requestInProgress = useLockStore((s) => s.requestInProgress);
  const outgoingRequestStatus = useLockStore((s) => s.outgoingRequestStatus);
  const { requestAccess } = useLockActions(formId, { onLockGranted });
  const { user } = useAuth();

  // requestInProgress can be set to OUR OWN email transiently: when we click
  // Request Access, the SignalR `accessRequested` push (which is broadcast to
  // the whole group, including the sender) often arrives back at this browser
  // faster than the HTTP response that would transition us to the overlay.
  // For the few render cycles in between, LockScreen is still mounted with
  // requestInProgress = us, which would otherwise flash a yellow "{us} is
  // currently requesting access" warning to ourselves before we transition.
  // Treat a self-match as effectively no other requester.
  const myEmail = user?.profile.email as string | undefined;
  const otherUserRequesting = !!requestInProgress &&
    (!myEmail || requestInProgress.toLowerCase() !== myEmail.toLowerCase());
  const buttonDisabled = otherUserRequesting;

  // After the holder denies, we land back here. Surface that explicitly so the
  // requester knows their previous attempt was rejected (not silently looped)
  // and that the holder is still active — re-requesting will trigger a fresh
  // 30s round, not an instant grant.
  const wasDenied = outgoingRequestStatus === "Denied";

  // Move keyboard focus to the Request Access button on mount so screen-reader
  // and keyboard-only users can act immediately without tabbing through the
  // form behind the overlay.
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={HEADING_ID}
      className="min-h-screen flex items-center justify-center bg-gray-50 px-6"
    >
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-warning-bg flex items-center justify-center mb-4">
          <Lock size={26} className="text-warning" />
        </div>

        <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2">Form In Use</h1>

        {wasDenied ? (
          <>
            <p className="text-gray-600 mb-1">
              <span className="font-semibold text-gray-800 break-words">{lockedBy ?? "Another user"}</span> is still actively editing this form.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You can request access again.
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-1">
              <span className="font-semibold text-gray-800 break-words">{lockedBy ?? "Another user"}</span> is currently filling out this form.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You can request access — they will have 30 seconds to respond.
            </p>
          </>
        )}

        {otherUserRequesting && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 mb-4 flex items-start gap-2 text-left">
            <AlertCircle size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              <span className="font-semibold break-words">{requestInProgress}</span> is currently requesting access. The button will re-enable when their request completes.
            </p>
          </div>
        )}

        <button
          ref={buttonRef}
          type="button"
          onClick={requestAccess}
          disabled={buttonDisabled}
          className="w-full py-2.5 px-4 rounded-md bg-brand-navy text-white font-semibold hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
        >
          Request Access
        </button>
      </div>
    </div>
  );
}
